import React, { Component } from 'react';
import { shallowEqual } from 'react-redux';
import PagerPage from '~/UI/Pager/Page/PagerPage';
import Pager from '~/UI/Pager/Pager';
import Portal from '~/UI/Portal/Portal';
import type SegmentedSelectPage from '../Page/SegmentedSelectPage';
import type { PageCreateProps } from '../Page/SegmentedSelectPage';
import type { SelectedOption } from '../SegmentedSelect';

interface Props {
	currentPageIndex: number;
	children: React.ReactElement<SegmentedSelectPage['props'], SegmentedSelectPage['render']>[] | React.ReactNode;
	referenceRect: ClientRect;
	width: number | null;
	selectedOption: SelectedOption | null;
	styles: { popover: string };
	createPageProps(page: React.ReactNode, index: number): PageCreateProps;
	onClose(event: MouseEvent): void;
}

interface State {
	rect: { width: number; left: number; bottom: number } | null;
}

export default class SegmentedSelectPopover extends Component<Props, State> {
	static defaultProps = {
		referenceRect: null,
		width: null,
		createPageProps: null,
	};

	state: State = {
		rect: null,
	};

	containerElement: HTMLDivElement | null = null;

	animationFrame: number | null = null;

	componentDidMount() {
		document.addEventListener('mousedown', this.handleDocumentClick, true);
		window.addEventListener('resize', this.windowResized, { passive: true });
		this.updateRect(this.props);
	}

	componentWillUnmount() {
		document.removeEventListener('mousedown', this.handleDocumentClick, true);
		window.removeEventListener('resize', this.windowResized);

		if (this.animationFrame !== null && window.cancelAnimationFrame) {
			window.cancelAnimationFrame(this.animationFrame);
			this.animationFrame = null;
		}
	}

	componentDidUpdate(prevProps: Props) {
		if (!shallowEqual(prevProps, this.props)) {
			this.setState({ rect: null });
		}

		if (this.state.rect === null) {
			this.updateRect(this.props);
		}
	}

	updateRect(props: Props) {
		const { children, referenceRect } = props;
		const staticWidth = React.Children.count(children) > 1;
		const width = Math.min(
			props.width || (staticWidth ? referenceRect.width + 2 : this.containerElement?.clientWidth || 0),
			window.innerWidth
		);
		const left = staticWidth ? referenceRect.left - 1 : referenceRect.left;
		const minLeft = 0;
		const maxLeft = window.innerWidth - width;
		const rect = {
			width,
			left: Math.max(minLeft, Math.min(maxLeft, left)),
			bottom: document.documentElement.scrollHeight - referenceRect.top,
		};

		this.setState({ rect });
	}

	handleDocumentClick = (event: MouseEvent) => {
		// @ts-expect-error Argument of type 'EventTarget | null' is not assignable to parameter of type 'Node | null'.
		if (event.target !== this.containerElement && !this.containerElement?.contains(event.target)) {
			this.props.onClose(event);
		}
	};

	windowResized = () => {
		if (this.animationFrame !== null && window.cancelAnimationFrame) {
			window.cancelAnimationFrame(this.animationFrame);
			this.animationFrame = null;
		}
		this.animationFrame = window.requestAnimationFrame(() => {
			if (this.animationFrame !== null) {
				this.setState({ rect: null });
			}
		});
	};

	render() {
		const {
			props: { children, currentPageIndex, styles, createPageProps },
			state: { rect },
		} = this;

		const style: {
			zIndex: number;
			visibility: 'hidden' | undefined;
			position: 'absolute';
		} = {
			zIndex: 200,
			visibility: rect === null ? 'hidden' : undefined,
			position: 'absolute',
			...(rect !== null ? rect : { bottom: 0 }),
		};

		return (
			<Portal>
				<div
					className={styles.popover}
					data-testid='segmented-select-popover'
					ref={(ref) => (this.containerElement = ref)}
					style={style}
				>
					{React.Children.count(children) > 1 ? (
						<Pager currentPageIndex={currentPageIndex}>
							{React.Children.map(
								// @ts-expect-error strange typing of obscure react methods
								children,
								(page: React.ReactElement<SegmentedSelectPage['props'], SegmentedSelectPage['render']>, pageIndex) => (
									<PagerPage>{React.cloneElement(page, createPageProps(page, pageIndex))}</PagerPage>
								)
							)}
						</Pager>
					) : (
						React.Children.map(children, (page, pageIndex) =>
							React.cloneElement(page as React.ReactElement, createPageProps(page, pageIndex))
						)
					)}
				</div>
			</Portal>
		);
	}
}
